import { create } from 'zustand'
import type {
  Logo,
  Background,
  Overlay,
  VideoClip,
  AudioClip,
} from '@vc/views/panels/BrandingPanel'

const genId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`

type MeetingBrandingState = {
  logos: Logo[]
  backgrounds: Background[]
  overlays: Overlay[]
  videoClips: VideoClip[]
  audioClips: AudioClip[]

  setLogos: (logos: Logo[]) => void
  setBackgrounds: (backgrounds: Background[]) => void
  setOverlays: (overlays: Overlay[]) => void
  setVideoClips: (clips: VideoClip[]) => void
  setAudioClips: (clips: AudioClip[]) => void

  addLogo: (logo: Omit<Logo, 'id' | 'createdAt'>) => void
  removeLogo: (logoId: string) => void
  addBackground: (background: Omit<Background, 'id' | 'createdAt'>) => void
  removeBackground: (backgroundId: string) => void
  toggleOverlay: (overlayId: string) => void
  addVideoClip: (clip: Omit<VideoClip, 'id' | 'createdAt'>) => void
  removeVideoClip: (videoClipId: string) => void
  addAudioClip: (clip: Omit<AudioClip, 'id' | 'createdAt'>) => void
  removeAudioClip: (audioClipId: string) => void
}

export const useMeetingBrandingStore = create<MeetingBrandingState>((set) => ({
  logos: [],
  backgrounds: [],
  overlays: [],
  videoClips: [],
  audioClips: [],

  setLogos: (logos) => set({ logos }),
  setBackgrounds: (backgrounds) => set({ backgrounds }),
  setOverlays: (overlays) => set({ overlays }),
  setVideoClips: (videoClips) => set({ videoClips }),
  setAudioClips: (audioClips) => set({ audioClips }),

  addLogo: (logo) =>
    set((s) => ({
      logos: [...s.logos, { ...logo, id: genId('logo'), createdAt: Date.now() }],
    })),
  removeLogo: (logoId) => set((s) => ({ logos: s.logos.filter((l) => l.id !== logoId) })),

  addBackground: (background) =>
    set((s) => ({
      backgrounds: [
        ...s.backgrounds,
        { ...background, id: genId('bg'), createdAt: Date.now() },
      ],
    })),
  removeBackground: (backgroundId) =>
    set((s) => ({ backgrounds: s.backgrounds.filter((b) => b.id !== backgroundId) })),

  toggleOverlay: (overlayId) =>
    set((s) => ({
      overlays: s.overlays.map((overlay) =>
        overlay.id === overlayId ? { ...overlay, isActive: !overlay.isActive } : overlay,
      ),
    })),

  addVideoClip: (clip) =>
    set((s) => ({
      videoClips: [
        ...s.videoClips,
        { ...clip, id: genId('vid'), createdAt: Date.now() },
      ],
    })),
  removeVideoClip: (videoClipId) =>
    set((s) => ({ videoClips: s.videoClips.filter((v) => v.id !== videoClipId) })),

  addAudioClip: (clip) =>
    set((s) => ({
      audioClips: [
        ...s.audioClips,
        { ...clip, id: genId('aud'), createdAt: Date.now() },
      ],
    })),
  removeAudioClip: (audioClipId) =>
    set((s) => ({ audioClips: s.audioClips.filter((a) => a.id !== audioClipId) })),
}))
